import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type {
  ExportBatch,
  ExportCandidate,
  ExportLine,
  ExportPreflight,
  PayPeriod,
  SageBridgeCompany,
  SageCompany,
} from '@/api/types'
import { useAuth } from '@/auth/AuthContext'

type Show = 'ready' | 'blocked' | 'sent' | 'all'

const SHOW: { key: Show; label: string }[] = [
  { key: 'ready', label: 'Ready' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'sent', label: 'Sent' },
  { key: 'all', label: 'All' },
]

/** "2026-11-03" -> "Tue 3 Nov", read as parts so it never shifts a day. */
function dayLabel(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(y, m - 1, d).getDay()]
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]
  return `${wd} ${d} ${mon}`
}

function costCode(c: string | null): string {
  return c && c.length === 9 ? `${c.slice(0, 3)}-${c.slice(3, 6)}-${c.slice(6)}` : c ?? '—'
}

function qty(c: { entry_type?: string; hours_worked: string | number | null; amount: string | number | null }): string {
  if (c.amount !== null && c.amount !== undefined) return `$${Number(c.amount).toFixed(2)}`
  if (c.hours_worked !== null && c.hours_worked !== undefined) return `${Number(c.hours_worked).toFixed(2)} h`
  return '—'
}

const LINE_BADGE: Record<ExportLine['status'], string> = {
  Approved: 'badge-info',
  Running: 'badge-warn',
  Done: 'badge-ok',
  Failed: 'badge-error',
  Cancelled: '',
}

/** Refused, then sent again in a later batch: history, not a to-do. */
function isReplaced(ln: ExportLine): boolean {
  return ln.status === 'Failed' && ln.sent_again_batch_id != null
}

const LINE_LABEL: Record<ExportLine['status'], string> = {
  Approved: 'Waiting',
  Running: 'Writing',
  Done: 'In Sage',
  Failed: 'Refused',
  Cancelled: 'Cancelled',
}

/**
 * Sage -> Send to Sage.
 *
 * Pre-flight a pay week: every line with exactly the values Sage will be
 * sent (tax profile and state, workers' comp, pay rate), and what stops a
 * line. Then send the ready lines to the bridge's queue and follow them.
 *
 * TST is the test company: a send there never locks or changes the real line.
 * NWL (live payroll) stays unavailable until the cutover is approved.
 * Plan: NWLining_Sage_TimeTrack_Bridge_Plan.md.
 */
export function SendToSagePage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'Admin'

  const [bridge, setBridge] = useState<SageBridgeCompany[]>([])
  const [company, setCompany] = useState<SageCompany>('TST')
  const [periods, setPeriods] = useState<PayPeriod[]>([])
  const [weekEnding, setWeekEnding] = useState('')
  const [pre, setPre] = useState<ExportPreflight | null>(null)
  const [batches, setBatches] = useState<ExportBatch[]>([])
  const [show, setShow] = useState<Show>('ready')
  // Batches whose replaced lines (refused, then sent again) are opened up.
  const [openReplaced, setOpenReplaced] = useState<Set<number>>(new Set())
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const fail = (err: unknown, fallback: string) => setError(err instanceof ApiError ? err.message : fallback)

  useEffect(() => {
    api.sageBridge().then(setBridge).catch((e) => fail(e, 'Could not load the Sage bridge status'))
    api
      .payPeriods()
      .then((p) => {
        setPeriods(p)
        if (p.length > 0) setWeekEnding(p[0].week_ending)
      })
      .catch((e) => fail(e, 'Could not load pay periods'))
  }, [])

  const load = useCallback(
    async (quiet = false) => {
      if (!weekEnding) return
      if (!quiet) {
        setPre(null)
        setError(null)
      }
      try {
        const [p, b, s] = await Promise.all([
          api.exportPreflight(company, weekEnding),
          api.exportBatches(company, weekEnding),
          api.sageBridge(),
        ])
        setPre(p)
        setBatches(b)
        setBridge(s)
        if (!quiet) setPicked(new Set(p.lines.filter((l) => l.sendable).map((l) => l.time_entry_id)))
      } catch (e) {
        fail(e, 'Could not load the week')
      }
    },
    [company, weekEnding],
  )

  useEffect(() => {
    void load()
  }, [load])

  // While anything is waiting or being written, follow it.
  const pending = batches.some((b) => (b.counts.Approved ?? 0) + (b.counts.Running ?? 0) > 0)
  useEffect(() => {
    if (!pending) return
    const t = window.setInterval(() => void load(true), 10000)
    return () => window.clearInterval(t)
  }, [pending, load])

  const here = bridge.find((b) => b.company === company)

  const shown = useMemo(() => {
    const lines = pre?.lines ?? []
    if (show === 'ready') return lines.filter((l) => l.sendable)
    if (show === 'blocked') return lines.filter((l) => l.problems.length > 0 && !l.sent_status)
    if (show === 'sent') return lines.filter((l) => l.sent_status)
    return lines
  }, [pre, show])

  const readyIds = (pre?.lines ?? []).filter((l) => l.sendable).map((l) => l.time_entry_id)
  const chosen = readyIds.filter((id) => picked.has(id))

  function toggle(id: string) {
    setPicked((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  async function send() {
    if (chosen.length === 0) return
    const target = company === 'TST' ? 'the TST test company' : 'LIVE PAYROLL (NWL)'
    if (!window.confirm(`Send ${chosen.length} line${chosen.length === 1 ? '' : 's'} to ${target}?`)) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const all = chosen.length === readyIds.length
      const b = await api.sendToSage(company, weekEnding, all ? null : chosen, note.trim() || null)
      setNote('')
      setNotice(
        `Sent ${b.line_count} line${b.line_count === 1 ? '' : 's'} to the queue for ${company}.` +
          (here?.writing_on ? '' : ` Writing to ${company} is switched off, so they will wait until it is on.`),
      )
      await load()
    } catch (e) {
      fail(e, 'Could not send')
    } finally {
      setBusy(false)
    }
  }

  async function act(fn: () => Promise<unknown>, done: string) {
    setError(null)
    setNotice(null)
    try {
      await fn()
      setNotice(done)
      await load(true)
    } catch (e) {
      fail(e, 'That did not work')
    }
  }

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      <div className="card">
        <h2 className="title" style={{ marginTop: 0 }}>Send to Sage</h2>
        <p className="muted">
          Check a pay week line by line, then send it to Sage Time Track. Each line shows exactly
          what Sage will get. A line with a problem is held back on its own; the rest can go.
          The Sage bridge on the Sage server writes them and reports back here.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label htmlFor="sx-company">Sage company</label>
            <select id="sx-company" value={company} onChange={(e) => setCompany(e.target.value as SageCompany)}>
              {(['TST', 'NWL'] as SageCompany[]).map((c) => {
                const b = bridge.find((x) => x.company === c)
                return (
                  <option key={c} value={c} disabled={b ? !b.allowed : c === 'NWL'}>
                    {c === 'TST' ? 'TST (test company)' : 'NWL (live payroll)'}
                    {c === 'NWL' && b && !b.allowed ? ' - not yet' : ''}
                  </option>
                )
              })}
            </select>
          </div>
          <div className="form-field" style={{ marginBottom: 0, minWidth: 240 }}>
            <label htmlFor="sx-week">Pay week</label>
            <select id="sx-week" value={weekEnding} onChange={(e) => setWeekEnding(e.target.value)}>
              {periods.map((p) => (
                <option key={p.week_ending} value={p.week_ending}>
                  {dayLabel(p.week_starting)} – {dayLabel(p.week_ending)}
                  {p.paid_in_sage ? ' · paid' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        {here && (
          <p className="muted" style={{ marginBottom: 0 }}>
            Bridge:{' '}
            {here.running ? (
              <span className="badge badge-ok">Running</span>
            ) : (
              <span className="badge badge-warn">Not running</span>
            )}{' '}
            {here.writing_on ? `writing to ${company} is on` : `writing to ${company} is switched off`}
            {here.waiting > 0 && ` · ${here.waiting} waiting`}
            {here.in_progress > 0 && ` · ${here.in_progress} being written`}
            {' · '}
            <Link to="/office/sage-bridge">Bridge status</Link>
          </p>
        )}
        {company === 'TST' && (
          <p className="muted" style={{ marginBottom: 0 }}>
            TST is a test: sending here does not lock or change the portal's lines.
          </p>
        )}
      </div>

      <div className="card">
        {pre === null ? (
          <p className="muted">Loading…</p>
        ) : (
          <>
            <div
              className="segmented"
              style={{ gridTemplateColumns: 'repeat(4, 1fr)', maxWidth: 480, marginBottom: '0.75rem' }}
            >
              {SHOW.map((s) => (
                <button key={s.key} type="button" aria-selected={show === s.key} onClick={() => setShow(s.key)}>
                  {s.label}{' '}
                  {s.key === 'ready' ? pre.ready : s.key === 'blocked' ? pre.blocked : s.key === 'sent' ? pre.already_sent : pre.lines.length}
                </button>
              ))}
            </div>
            {shown.length === 0 ? (
              <p className="muted">
                {pre.lines.length === 0 ? 'No time recorded in this week.' : 'Nothing in this view.'}
              </p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th />
                      <th>Crew member</th>
                      <th>Day</th>
                      <th>Job</th>
                      <th>Work type</th>
                      <th>Hours / amount</th>
                      <th>Tax profile</th>
                      <th>Workers' comp</th>
                      <th>Pay rate</th>
                      <th>Check</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((l: ExportCandidate) => (
                      <tr key={l.time_entry_id}>
                        <td>
                          {l.sendable && (
                            <input
                              type="checkbox"
                              aria-label="Send this line"
                              checked={picked.has(l.time_entry_id)}
                              onChange={() => toggle(l.time_entry_id)}
                            />
                          )}
                        </td>
                        <td>
                          {l.display_name}
                          <div className="muted mono">{l.employee_display}</div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{dayLabel(l.date_worked)}</td>
                        <td className="mono">{l.job_no}</td>
                        <td>
                          {l.work_type_label ?? l.earnings_code}
                          <div className="muted mono">{costCode(l.cost_code)} · {l.earnings_code}</div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{qty(l)}</td>
                        <td className="mono" style={{ whiteSpace: 'pre' }}>
                          {l.tax_profile ?? '—'}
                          {l.tax_state && <div className="muted">state {l.tax_state}</div>}
                        </td>
                        <td>
                          {l.workers_comp === '' ? 'None' : l.workers_comp ?? <span className="muted">Sage default</span>}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {l.pay_rate !== null ? (
                            <>
                              ${Number(l.pay_rate).toFixed(2)}
                              <div className="muted">{l.rate_source}</div>
                            </>
                          ) : l.entry_type === 'Amount' ? (
                            '—'
                          ) : (
                            <span className="muted">Sage default</span>
                          )}
                        </td>
                        <td>
                          {l.sent_status && (
                            <span className="badge badge-info">
                              {l.sent_status === 'Done' || l.sent_status === 'Posted' ? 'In Sage' : 'Sent'}
                            </span>
                          )}
                          {l.problems.map((p) => (
                            <div key={p} style={{ color: 'var(--c-error)' }}>{p}</div>
                          ))}
                          {l.warnings.map((w) => (
                            <div key={w} className="muted">{w}</div>
                          ))}
                          {l.sendable && l.warnings.length === 0 && <span className="badge badge-ok">Ready</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {pre.ready > 0 && (
              <div
                style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '0.75rem' }}
              >
                <div className="form-field" style={{ marginBottom: 0, flex: '1 1 240px' }}>
                  <label htmlFor="sx-note">Note (optional)</label>
                  <input
                    id="sx-note"
                    maxLength={300}
                    placeholder="e.g. first test week"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
                <button className="btn" disabled={busy || chosen.length === 0} onClick={() => void send()}>
                  {busy ? 'Sending…' : `Send ${chosen.length} line${chosen.length === 1 ? '' : 's'} to ${company}`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {batches.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Sent this week</h2>
          {batches.map((b) => {
            // A refused line that has since been sent again is history, not a
            // problem: it is folded away, and a batch whose only refusals were
            // all sent again reads as Done (TST, 4 Nov 2026: batch 17 looked
            // half-broken after batch 24 had put every one of its lines in Sage).
            const lines = b.lines ?? []
            const nReplaced = lines.filter(isReplaced).length
            const into = [...new Set(lines.filter(isReplaced).map((ln) => ln.sent_again_batch_id))].sort((x, y) => Number(x) - Number(y))
            const intoText = `batch${into.length === 1 ? '' : 'es'} ${into.join(', ')}`
            const status = b.status === 'Partial' && nReplaced > 0 && (b.counts.Failed ?? 0) === nReplaced ? 'Done' : b.status
            const opened = openReplaced.has(b.batch_id)
            const visible = opened ? lines : lines.filter((ln) => !isReplaced(ln))
            return (
            <div key={b.batch_id} style={{ marginBottom: '1rem' }}>
              <p style={{ marginBottom: '0.5rem' }}>
                <strong>Batch {b.batch_id}</strong>{' '}
                <span className={`badge ${status === 'Done' ? 'badge-ok' : status === 'Partial' ? 'badge-warn' : 'badge-info'}`}>
                  {status === 'Open' ? 'In progress' : status}
                </span>{' '}
                <span className="muted">
                  {b.line_count} line{b.line_count === 1 ? '' : 's'}
                  {nReplaced > 0 && ` · ${nReplaced} refused at first, then sent again in ${intoText}`} · sent by {b.approved_by ?? '—'}{' '}
                  {new Date(b.approved_utc + 'Z').toLocaleString(undefined, {
                    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                  })}
                  {b.note && ` · ${b.note}`}
                </span>{' '}
                {(b.counts.Approved ?? 0) > 0 && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => void act(() => api.cancelExportBatch(b.batch_id), 'Cancelled the waiting lines.')}
                  >
                    Cancel waiting lines
                  </button>
                )}
              </p>
              {visible.length > 0 && (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Crew member</th>
                      <th>Day</th>
                      <th>Job</th>
                      <th>Work type</th>
                      <th>Hours / amount</th>
                      <th>Status</th>
                      <th>Sage says</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((ln) => {
                      const replaced = isReplaced(ln)
                      const refused = ln.status === 'Failed' && !replaced
                      return (
                      <tr key={ln.export_line_id} style={replaced ? { opacity: 0.6 } : undefined}>
                        <td>
                          {ln.display_name ?? '—'}
                          <div className="muted mono">{ln.employee_display}</div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{dayLabel(ln.date_worked)}</td>
                        <td className="mono">{ln.job_no}</td>
                        <td>{ln.work_type_label ?? '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{qty(ln)}</td>
                        <td>
                          {replaced ? (
                            <span className="badge">Replaced</span>
                          ) : (
                            <span className={`badge ${LINE_BADGE[ln.status]}`}>{LINE_LABEL[ln.status]}</span>
                          )}
                          {/* Only a line in Sage has a real Sage number. */}
                          {ln.status === 'Done' && ln.sage_sequence_no && (
                            <div className="muted mono">seq {ln.sage_sequence_no}</div>
                          )}
                        </td>
                        <td className={refused ? undefined : 'muted'} style={refused ? { color: 'var(--c-error)' } : undefined}>
                          {replaced && 'First try: '}
                          {ln.result_message ?? '—'}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {ln.status === 'Approved' && (
                            <button
                              className="btn btn-secondary"
                              onClick={() => void act(() => api.cancelExportLine(ln.export_line_id), 'Line cancelled.')}
                            >
                              Cancel
                            </button>
                          )}
                          {ln.status === 'Failed' && ln.sent_again_batch_id && (
                            <span className="muted">Sent again in batch {ln.sent_again_batch_id}</span>
                          )}
                          {ln.status === 'Failed' && !ln.sent_again_batch_id && (
                            <button
                              className="btn btn-secondary"
                              onClick={() => void act(() => api.retryExportLine(ln.export_line_id), 'Line sent again.')}
                            >
                              Retry
                            </button>
                          )}
                        </td>
                      </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              )}
              {nReplaced > 0 && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ marginTop: '0.5rem' }}
                  onClick={() =>
                    setOpenReplaced((s0) => {
                      const n = new Set(s0)
                      if (n.has(b.batch_id)) n.delete(b.batch_id)
                      else n.add(b.batch_id)
                      return n
                    })
                  }
                >
                  {opened
                    ? 'Hide the replaced lines'
                    : `Show the ${nReplaced} line${nReplaced === 1 ? '' : 's'} replaced by ${intoText}`}
                </button>
              )}
            </div>
            )
          })}
          {company === 'TST' && isAdmin && (
            <p className="muted" style={{ marginBottom: 0 }}>
              After TST is refreshed from NWL these results no longer mean anything.{' '}
              <button
                className="btn btn-secondary"
                onClick={() => {
                  if (window.confirm('Forget this week\'s TST results so the lines can be sent to TST again?'))
                    void act(() => api.forgetTstSends(weekEnding), 'TST results for this week forgotten.')
                }}
              >
                Forget TST results for this week
              </button>
            </p>
          )}
        </div>
      )}
    </div>
  )
}
